import {
    Component,
    ContainerSpecification,
    createContainer,
    createSpine,
    Spine,
    SpineSpecification,
} from '@1x2network/pixi_assets'
import { SymbolFetcher } from '@1x2network/reel_set/lib/composite/components/fetcher/SymbolFetcher'
import { Container, Point } from 'pixi.js'
import { CustomSymbol } from '../CustomSymbol'
import gsap from 'gsap'
import { TradReelSet } from '@1x2network/reel_set'
import { FromToVars } from '@1x2network/tweens/lib/types/common'
import { staggerTimeline } from '../utils/gsap'

export class Anticipation extends Component<AnticipationSpecification> {
    private spine: Spine
    private reelSet: TradReelSet<CustomSymbol>
    private fetcher: SymbolFetcher<CustomSymbol>
    public cleanupMap = new Map<CustomSymbol, [GSAPTimeline, GSAPTimeline, Container]>()
    private firstAnimation?: GSAPTimeline

    private _isActive = false
    public get isActive() {
        return this._isActive
    }

    private _currentReelIndex = -1
    public get currentReelIndex() {
        return this._currentReelIndex
    }

    constructor(options: { reelSet: TradReelSet<CustomSymbol>; parent: Container }) {
        super({ spec: 'anticipation', parent: options.parent })

        this.reelSet = options.reelSet
        this.fetcher = this.reelSet.fetcher
        this.spine = createSpine({ spec: this.specification.asset, parent: this })
    }

    public anticipateReel(reelIndex: number): void {
        this._isActive = true
        this._currentReelIndex = reelIndex
        const symbols = this.fetcher.getAllSymbolsByReel(reelIndex)
        const averagePosition = symbols
            .map((symbol) => symbol.position)
            .reduce((acc, pos) => acc.add(pos, acc), new Point())
            .multiplyScalar(1 / symbols.length)

        this.spine.position.x = averagePosition.x
        this.spine.play('animation', 0, true)
        this.spine.visible = true
    }

    private symbolAnimation(symbol: CustomSymbol): [GSAPTimeline, GSAPTimeline, Container] {
        // EffectSymbols will be cleaned up later by accessing the tween's targets directly
        // This is because the stagger makes it really difficult to clean up the effect symbols nicely
        const effectSymbols: CustomSymbol[] = []
        for (let i = 0; i < 2; i++) {
            const effectSymbol = this.reelSet.pool.requestLandSymbol({ id: symbol.id })
            effectSymbol.name = 'EffectSymbol'
            effectSymbol.blurred = false
            effectSymbols.push(effectSymbol)
        }

        const container = createContainer({ spec: { zIndex: -1 }, parent: symbol })
        container.addChild(...effectSymbols)

        const { scaleFromTo: scaleConfig, effectFromTo: effectConfig } = this.specification.tweens
        const spine = symbol.spine as Spine
        const scaleTimeline = gsap.timeline({ repeat: -1 }).fromTo(spine, scaleConfig.from, scaleConfig.to)

        const effectTweens: GSAPTween[] = []
        for (let i = 0; i < 2; i++) {
            effectSymbols[i].visible = false
            effectTweens.push(
                gsap.fromTo(effectSymbols[i], effectConfig.from, {
                    ...effectConfig.to,
                    immediateRender: false,
                    onStart: () => {
                        effectSymbols[i].visible = true
                    },
                }),
            )
        }
        const effectTimeline = staggerTimeline(effectTweens, 1)

        if (this.firstAnimation) {
            scaleTimeline.totalTime(this.firstAnimation.totalTime())
            effectTimeline.totalTime(this.firstAnimation.totalTime())
        }

        return [scaleTimeline, effectTimeline, container]
    }

    public anticipateSymbols(symbols: CustomSymbol[]): void {
        symbols.forEach((symbol) => {
            if (this.cleanupMap.has(symbol)) {
                return
            }

            const animations = this.symbolAnimation(symbol)
            this.cleanupMap.set(symbol, animations)
            if (!this.firstAnimation) {
                this.firstAnimation = animations[0]
            }
        })
    }

    public stop() {
        this._isActive = false
        this._currentReelIndex = -1
        this.firstAnimation = undefined
        this.spine.reset()
        this.spine.visible = false

        for (const [symbol, tweens] of Array.from(this.cleanupMap.entries())) {
            const [scaleTimeline, effectTimeline, container] = tweens
            this.cleanupMap.delete(symbol)
            scaleTimeline.repeat(scaleTimeline.iteration() - 1)

            const promises = effectTimeline.getChildren().map(async (child) => {
                child.repeat(child.iteration() - 1)
                if (child.isActive()) {
                    await child
                }

                const targets = child.targets()
                for (const target of targets) {
                    gsap.killTweensOf(target) // Sometimes gsap updates properties after the tween is finished ???
                    this.reelSet.pool.returnSymbol(target as CustomSymbol)
                }
            })

            Promise.all(promises).then(() => {
                container.destroy()
                effectTimeline.kill()
            })
        }
    }
}

export interface AnticipationSpecification extends ContainerSpecification {
    asset: SpineSpecification
    tweens: {
        scaleFromTo: FromToVars
        effectFromTo: FromToVars
    }
}
